import { Component, Input, NgZone, OnChanges, SimpleChanges } from '@angular/core';
import { EChartsType, EChartsOption } from 'echarts/types/dist/shared';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { SVGRenderer } from 'echarts/renderers';
import { DsColors } from '@levelaccess/design-system';
import { SeverityRankColourMap } from '@client/app/shared/colors';

import { IScanSummary } from '../../../../../../../shared/interfaces/scan-summaries.interface';
import { $severity } from '../../../../../../../shared/constants/accessibility';
import { TranslateService } from '../../../../../translate/translate.service';
import { AccessibilityAuditToolNames } from '../../../../../../../shared/constants/audit-tool';
import { SharedAuditsUtility } from '../../../../../../../shared/utils/audits.utility';
import { $scanSummaries } from '../../../../../../../shared/constants/scan-summaries';
import { IScanAudit } from '../../../../../../../shared/interfaces/scan-audit.interface';
import {
  severityColors,
  TOOLTIP_BACKGROUND_COLOR,
  TOOLTIP_TEXT_COLOR,
  DECAL_COLOR,
} from '../../../../../constants/charts.constants';
import { ICustomSeveritiesMap } from '../../../../../../../shared/interfaces/tenant.interface';
import { $customSeverity } from '../../../../../../../shared/constants/tenant';
import { EapColors } from '../../../../../shared/eap-colors';
import { SharedTextUtility } from '../../../../../../../shared/utils/text.utility';
import { EchartsService } from '../../../../../services/echarts.service';

@Component({
  selector: 'app-view-scan-results-issues-by-severity',
  templateUrl: './view-scan-results-issues-by-severity.component.html',
  styleUrls: ['./view-scan-results-issues-by-severity.component.scss'],
  providers: [EchartsService],
  standalone: false,
})
export class ViewScanResultsIssuesBySeverityComponent implements OnChanges {
  private severityCount: Partial<Record<$severity, number>>;
  private yAxisLabels: string[];

  public barChart: EChartsType;

  @Input() public scanSummary: IScanSummary;
  @Input() public auditTool: AccessibilityAuditToolNames;
  @Input() public customSeverities: ICustomSeveritiesMap;

  constructor(
    private translateService: TranslateService,
    private ngZone: NgZone,
    private echartsService: EchartsService,
  ) {
    this.severityCount = {
      [$severity.critical]: 0,
      [$severity.high]: 0,
      [$severity.low]: 0,
      [$severity.info]: 0,
    };
  }

  private getSeverityCount(scanSummary: IScanSummary): void {
    this.severityCount = SharedAuditsUtility.getSeverityCount(
      this.auditTool,
      scanSummary?.[$scanSummaries.scanAuditsId] as IScanAudit,
      scanSummary?.[$scanSummaries.metrics],
    );
  }

  private getSeverityColor(severity: $severity): string | EapColors | DsColors {
    if (this.customSeverities?.has(severity)) {
      return SeverityRankColourMap.get(this.customSeverities.get(severity)[$customSeverity.rank]);
    }

    return severityColors[severity];
  }

  private getSeverityLabel(severity: $severity): string {
    if (this.customSeverities?.has(severity)) {
      return SharedTextUtility.capitalize(this.customSeverities.get(severity)[$customSeverity.label]);
    }

    return this.translateService.instant(`scan_issue_severity_${severity}`);
  }

  private getSeverityLabels(): string[] {
    return [
      this.getSeverityLabel($severity.critical),
      this.getSeverityLabel($severity.high),
      this.getSeverityLabel($severity.low),
    ];
  }

  private drawSeverityChart(): void {
    const options: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: TOOLTIP_BACKGROUND_COLOR,
        textStyle: {
          color: TOOLTIP_TEXT_COLOR,
        },
        axisPointer: {
          type: 'none',
        },
      },
      legend: {
        itemStyle: {
          color: 'black',
        },
        itemWidth: 12,
        itemHeight: 12,
        textStyle: { fontWeight: 'bold' },
        show: true,
        icon: 'rect',
        left: 0,
      },
      grid: {
        left: 0,
        right: 0,
        top: 15,
        bottom: 20,
        height: 250,
        containLabel: true,
      },
      animation: false,
      xAxis: {
        type: 'value',
        boundaryGap: [0, 0.01],
        splitNumber: 5,
      },
      yAxis: {
        type: 'category',
        data: this.yAxisLabels,
        inverse: true,
        axisTick: {
          show: false,
        },
        axisLabel: {
          fontWeight: 'bold',
          color: '#000',
        },
        axisLine: {
          show: false,
        },
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 30,
          data: [
            {
              value: this.severityCount[$severity.critical],
              itemStyle: {
                color: this.getSeverityColor($severity.critical),
              },
              label: {
                fontWeight: 700,
                fontSize: 14,
                color: this.getSeverityColor($severity.critical),
                position: [5, -25],
              },
            },
            {
              value: this.severityCount[$severity.high],
              itemStyle: {
                color: this.getSeverityColor($severity.high),
                borderWidth: 1,
                borderColor: DECAL_COLOR,
                decal: {
                  color: DECAL_COLOR,
                  ...(this.customSeverities?.has($severity.high)
                    ? {
                        dashArrayX: [1, 0],
                        dashArrayY: [2, 5],
                        rotation: Math.PI / 4,
                      }
                    : {
                        dashArrayX: [1, 3],
                        dashArrayY: [1, 0],
                        rotation: 0,
                      }),
                },
              },
              label: {
                fontWeight: 700,
                fontSize: 14,
                color: this.getSeverityColor($severity.high),
                position: [5, -25],
              },
            },
            {
              value: this.severityCount[$severity.low],
              itemStyle: {
                color: this.getSeverityColor($severity.low),
                borderWidth: 1,
                borderColor: DECAL_COLOR,
                decal: {
                  ...(this.customSeverities?.has($severity.low)
                    ? {
                        color: DsColors.White,
                        symbol: 'circle',
                        symbolSize: 0.5,
                      }
                    : {
                        color: DECAL_COLOR,
                        dashArrayX: [1, 15],
                        dashArrayY: [1, 0],
                        rotation: 0,
                      }),
                },
              },
              label: {
                fontWeight: 700,
                fontSize: 14,
                color: this.getSeverityColor($severity.low),
                position: [5, -25],
              },
            },
          ],
          label: {
            show: true,
            position: 'top',
          },
        },
      ],
      aria: {
        enabled: true,
        decal: {
          show: true,
        },
      },
    };

    echarts.use([GridComponent, TooltipComponent, LegendComponent, BarChart, SVGRenderer]);

    this.barChart = this.echartsService.initialize(document.getElementById('severityChart'));
    this.barChart.setOption(options);
  }

  public getChartAriaLabel(): string {
    return this.translateService.instant('scan_severity_chart_aria_label', [
      this.getSeverityLabel($severity.critical),
      this.severityCount[$severity.critical],
      this.getSeverityLabel($severity.high),
      this.severityCount[$severity.high],
      this.getSeverityLabel($severity.low),
      this.severityCount[$severity.low],
    ]);
  }

  public ngOnChanges(_: SimpleChanges): void {
    this.getSeverityCount(this.scanSummary);
    this.yAxisLabels = this.getSeverityLabels();
    // needed or else the chart doesn't appear when switching tools
    this.ngZone.runOutsideAngular(() => setTimeout(this.drawSeverityChart.bind(this)));
  }
}
